import { GED_TYPE } from '@/shared/constants/ged';
import type { GetCnDataResponse, GetCnDataRule } from '@/shared/DTO/GED';

export function useFileHelper() {
    return {
        getIcon,
        getFileSize,
        getRule,
    };
}

function getFileSize(bytes: number): string {
    if (bytes >= 1_073_741_824) {
        return `${Math.round(bytes / 1_073_741_824)}Go`;
    }
    if (bytes >= 1_048_576)     {
        return `${Math.round(bytes / 1_048_576)}Mo`;
    }
    if (bytes >= 1_024)         {
        return `${Math.round(bytes / 1_024)}ko`;
    }
    return `${bytes}o`;
}

const ICON_MAP_ITEM_TYPE: Partial<Record<number, string>> = {
    [GED_TYPE.PENDING_DOCUMENT]: 'ax ax-extension-document-attendu',
    [GED_TYPE.WORKSHEET]: 'ax ax-fiche-1',
};

const ICON_MAP_FORMAT: Record<string, string> = {
    // PDF
    'pdf': 'ax ax-extension-pdf',

    // Excel
    'xls': 'ax ax-extension-xls',
    'xlsx': 'ax ax-extension-xls',
    'xlsm': 'ax ax-extension-xls',

    // Word
    'doc': 'ax ax-extension-doc',
    'docx': 'ax ax-extension-doc',
    'docm': 'ax ax-extension-doc',
    'dotm': 'ax ax-extension-doc',
    'dotx': 'ax ax-extension-doc',
    'odt': 'ax ax-extension-doc',

    // Powerpoint
    'ppt': 'ax ax-extension-ppt',
    'pptx': 'ax ax-extension-ppt',
    'pptm': 'ax ax-extension-ppt',
    'potm': 'ax ax-extension-ppt',
    'potx': 'ax ax-extension-ppt',

    // Presentation
    'key': 'ax ax-extension-presentation',
    'odp': 'ax ax-extension-presentation',

    // Data
    'csv': 'ax ax-extension-data',
    'sql': 'ax ax-extension-data',
    'mbd': 'ax ax-extension-data',
    'accdb': 'ax ax-extension-data',
    'db': 'ax ax-extension-data',
    'sqlite': 'ax ax-extension-data',
    'ods': 'ax ax-extension-data',
    'gsheet': 'ax ax-extension-data',
    'tab': 'ax ax-extension-data',
    'tsv': 'ax ax-extension-data',
    'xltx': 'ax ax-extension-data',
    'xltm': 'ax ax-extension-data',

    // Documents
    'rft': 'ax ax-extension-documents',
    'txt': 'ax ax-extension-documents',
    'text': 'ax ax-extension-documents',
    'md': 'ax ax-extension-documents',
    'pages': 'ax ax-extension-documents',
    'epub': 'ax ax-extension-documents',
    'wpd': 'ax ax-extension-documents',
    'wps': 'ax ax-extension-documents',
    'abw': 'ax ax-extension-documents',
    'fb2': 'ax ax-extension-documents',
    'djvu': 'ax ax-extension-documents',
    'pdb': 'ax ax-extension-documents',
    'ibooks': 'ax ax-extension-documents',
    'cbr': 'ax ax-extension-documents',
    'cbz': 'ax ax-extension-documents',
    'chm': 'ax ax-extension-documents',
    'xps': 'ax ax-extension-documents',
    'prc': 'ax ax-extension-documents',
    'mlk': 'ax ax-extension-documents',

    // ZIP
    'zip': 'ax ax-extension-zip',
    'rar': 'ax ax-extension-zip',
    '7z': 'ax ax-extension-zip',
    'tar.gz': 'ax ax-extension-zip',
    'tar': 'ax ax-extension-zip',
    'gz': 'ax ax-extension-zip',
    'bz2': 'ax ax-extension-zip',
    'xz': 'ax ax-extension-zip',
    'rpm': 'ax ax-extension-zip',
    'arj': 'ax ax-extension-zip',
    'deb': 'ax ax-extension-zip',
    'pkg': 'ax ax-extension-zip',

    // Images
    'jpg': 'ax ax-extension-image',
    'jpeg': 'ax ax-extension-image',
    'png': 'ax ax-extension-image',
    'gif': 'ax ax-extension-image',
    'bmp': 'ax ax-extension-image',
    'tif': 'ax ax-extension-image',
    'tiff': 'ax ax-extension-image',
    'eps': 'ax ax-extension-image',
    'svg': 'ax ax-extension-image',
    'psd': 'ax ax-extension-image',
    'ai': 'ax ax-extension-image',
    'xcf': 'ax ax-extension-image',

    // Video
    'mp4': 'ax ax-extension-video',
    'avi': 'ax ax-extension-video',
    'mov': 'ax ax-extension-video',
    'mkv': 'ax ax-extension-video',
    'wmv': 'ax ax-extension-video',
    'flw': 'ax ax-extension-video',
    'mpeg': 'ax ax-extension-video',
    'mpg': 'ax ax-extension-video',
    'webm': 'ax ax-extension-video',
    '3gp': 'ax ax-extension-video',
    '3g2': 'ax ax-extension-video',
    'flv': 'ax ax-extension-video',
    'm4v': 'ax ax-extension-video',
    'rm': 'ax ax-extension-video',
    'avchd': 'ax ax-extension-video',
    'asf': 'ax ax-extension-video',
    'qt': 'ax ax-extension-video',

    // Audio
    'mp3': 'ax ax-extension-audio',
    'wav': 'ax ax-extension-audio',
    'flac': 'ax ax-extension-audio',
    'aac': 'ax ax-extension-audio',
    'wma': 'ax ax-extension-audio',
    'ogg': 'ax ax-extension-audio',
    'm4a': 'ax ax-extension-audio',
    'aiff': 'ax ax-extension-audio',
    'ape': 'ax ax-extension-audio',
    'opus': 'ax ax-extension-audio',
    'mka': 'ax ax-extension-audio',
    'ac3': 'ax ax-extension-audio',
    'amr': 'ax ax-extension-audio',
    'mid': 'ax ax-extension-audio',
    'midi': 'ax ax-extension-audio',
    'ra': 'ax ax-extension-audio',
    'ram': 'ax ax-extension-audio',
    'au': 'ax ax-extension-audio',
    'dts': 'ax ax-extension-audio',
    'pcm': 'ax ax-extension-audio',
    'spx': 'ax ax-extension-audio',
    'wv': 'ax ax-extension-audio',
    'weba': 'ax ax-extension-audio',
    'awb': 'ax ax-extension-audio',
    'mod': 'ax ax-extension-audio',
    'xm': 'ax ax-extension-audio',
    'mtm': 'ax ax-extension-audio',
    'mo3': 'ax ax-extension-audio',
    's3m': 'ax ax-extension-audio',
    'umx': 'ax ax-extension-audio',
    'vqf': 'ax ax-extension-audio',
    'sid': 'ax ax-extension-audio',
    'w64': 'ax ax-extension-audio',
    'snd': 'ax ax-extension-audio',
    'aifc': 'ax ax-extension-audio',
    'cda': 'ax ax-extension-audio',
    'gsm': 'ax ax-extension-audio',
    'ivs': 'ax ax-extension-audio',
    'mpc': 'ax ax-extension-audio',
    'oma': 'ax ax-extension-audio',
    'm4b': 'ax ax-extension-audio',
    'm4p': 'ax ax-extension-audio',

    // Email
    'eml': 'ax ax-extension-mail',
    'emlx': 'ax ax-extension-mail',
    'msg': 'ax ax-extension-mail',
    'pst': 'ax ax-extension-mail',
    'mbox': 'ax ax-extension-mail',
    'oft': 'ax ax-extension-mail',
    'email': 'ax ax-extension-mail',
    'ost': 'ax ax-extension-mail',
    'vcf': 'ax ax-extension-mail',
    'edb': 'ax ax-extension-mail',
    'ics': 'ax ax-extension-mail',
    'olm': 'ax ax-extension-mail',
    'p7s': 'ax ax-extension-mail',
    'rpmsg': 'ax ax-extension-mail',
    'tnef': 'ax ax-extension-mail',

    // BIM
    'ifc': 'ax ax-extension-ifc',
    'ifcxml': 'ax ax-extension-ifc',
    'ifczip': 'ax ax-extension-ifc-zip',
    'rvt': 'ax ax-extension-rvt',
    'nwd': 'ax ax-extension-nwd',
    'nwc': 'ax ax-extension-nwc',
    'dwg': 'ax ax-extension-dwg',
    'dxf': 'ax ax-extension-dxf',

    // BCF
    'bcf': 'ax ax-extension-bcf',
    'bcfzip': 'ax ax-extension-bcf',

    // Overlay
    'ovl': 'ax ax-extension-ovl',

    // Geometry
    'dwf': 'ax ax-extension-geometry',
    'dxg': 'ax ax-extension-geometry',
    'step': 'ax ax-extension-geometry',
    'iges': 'ax ax-extension-geometry',
    'obj': 'ax ax-extension-geometry',
    'fbx': 'ax ax-extension-geometry',
    'catpart': 'ax ax-extension-geometry',
    'catproduct': 'ax ax-extension-geometry',
    'sat': 'ax ax-extension-geometry',
    'sab': 'ax ax-extension-geometry',
    'brep': 'ax ax-extension-geometry',
    'dae': 'ax ax-extension-geometry',
    'gltf': 'ax ax-extension-geometry',
    'glb': 'ax ax-extension-geometry',
    'x_t': 'ax ax-extension-geometry',
    'x_b': 'ax ax-extension-geometry',
    '3dm': 'ax ax-extension-geometry',
    'stp': 'ax ax-extension-geometry',
    'u3d': 'ax ax-extension-geometry',
    'usda': 'ax ax-extension-geometry',
    'usdc': 'ax ax-extension-geometry',
    'usdz': 'ax ax-extension-geometry',
    'wrl': 'ax ax-extension-geometry',
    'igs': 'ax ax-extension-geometry',
    'prt': 'ax ax-extension-geometry',
    'blend': 'ax ax-extension-geometry',
    '3ds': 'ax ax-extension-geometry',
    'ply': 'ax ax-extension-geometry',
    'dgn': 'ax ax-extension-geometry',
    'pln': 'ax ax-extension-geometry',

    // Geospatial
    'shp': 'ax ax-extension-geospatial',
    'geotiff': 'ax ax-extension-geospatial',
    'kml': 'ax ax-extension-geospatial',
    'kmz': 'ax ax-extension-geospatial',
    'geojson': 'ax ax-extension-geospatial',
    'las': 'ax ax-extension-geospatial',
    'laz': 'ax ax-extension-geospatial',

    // Manufacturing
    'amf': 'ax ax-extension-manufacturing',
    '3mf': 'ax ax-extension-manufacturing',
    'gcode': 'ax ax-extension-manufacturing',
    'x3d': 'ax ax-extension-manufacturing',
    'stl': 'ax ax-extension-manufacturing',

    // Install
    'nrg': 'ax ax-extension-install',
    'dmg': 'ax ax-extension-install',
    'vdmk': 'ax ax-extension-install',
    'exe': 'ax ax-extension-install',

    // Font
    'ttf': 'ax ax-extension-font',
    'otf': 'ax ax-extension-font',
    'woff': 'ax ax-extension-font',
    'eot': 'ax ax-extension-font',

    // Dev
    'html': 'ax ax-extension-dev',
    'xml': 'ax ax-extension-dev',
    'mht': 'ax ax-extension-dev',
    'css': 'ax ax-extension-dev',
    'js': 'ax ax-extension-dev',
    'php': 'ax ax-extension-dev',
    'json': 'ax ax-extension-dev',
    'c': 'ax ax-extension-dev',
    'cgi': 'ax ax-extension-dev',
    'pl': 'ax ax-extension-dev',
    'class': 'ax ax-extension-dev',
    'cpp': 'ax ax-extension-dev',
    'cs': 'ax ax-extension-dev',
    'h': 'ax ax-extension-dev',
    'java': 'ax ax-extension-dev',
    'py': 'ax ax-extension-dev',
    'sh': 'ax ax-extension-dev',
    'swift': 'ax ax-extension-dev',
    'vd': 'ax ax-extension-dev',

    // Link
    'link': 'ax ax-extension-link',
};

function getIcon(format: string | null | undefined, itemType?: number): string {
    if (format) {
        const normalizedFormat = format.toLowerCase().replace('.', '');
        const formatIcon = ICON_MAP_FORMAT[normalizedFormat];
        if (formatIcon) {
            return formatIcon;
        }
    }

    if (itemType !== undefined && ICON_MAP_ITEM_TYPE[itemType]) {
        return ICON_MAP_ITEM_TYPE[itemType]!;
    }

    return 'ax ax-extension-unknown';
}

function getRule(fileExtension: string, cnData: GetCnDataResponse) {
    if (cnData.rules.length === 0) {
        return null;
    }

    const ruleByFormat = cnData.rules.find((rule: GetCnDataRule) => rule.format.includes(fileExtension.toLowerCase()));
    if (ruleByFormat) {
        return ruleByFormat;
    }

    const ruleByExtension = cnData.rules.find((rule: GetCnDataRule) => rule.format[0] === '.+');
    if (ruleByExtension) {
        return ruleByExtension;
    }

    return null;
}
